"use client"

import type { VariantProps } from "class-variance-authority"
import type { ReactNode } from "react"
import type { TextareaAutosizeProps } from "react-textarea-autosize"
import { forwardRef, memo } from "react"
import { cva } from "class-variance-authority"
import { AnimatePresence, motion } from "motion/react"
import { IconCircleWarningOutline18 as AlertCircleIcon } from "nucleo-ui-outline-18"
import TextareaAutosize from "react-textarea-autosize"

import { cn } from "@productlane/lib/src/browser"

import { Label } from "../label"

const textareaVariants = cva(
	"text-sm flex transition w-full min-h-full placeholder:text-tertiary bg-white dark:bg-transparent focus:outline-none disabled:cursor-not-allowed resize-none",
	{
		variants: {
			variant: {
				outline:
					"px-3 py-2 ring-1 ring-gray-900/15 rounded-md dark:ring-white/15",
				settings:
					"px-3.5 py-3 ring-1 ring-gray-900/15 rounded-[10px] dark:ring-white/15",
				portal: "p-4 ring-1 ring-gray-900/20  rounded-md dark:ring-white/10 ",
				borderless:
					"border-transparent focus:outline-none focus:ring-0 bg-transparent resize-none",
			},
			textSize: {
				sm: "text-sm",
				md: "text-md",
				xl: "text-xl",
				lg: "text-lg",
				xxl: "text-[2rem]",
			},
			fontWeight: {
				normal: "font-normal",
				medium: "font-medium",
				semibold: "font-semibold",
				bold: "font-bold",
			},
		},
		defaultVariants: {
			textSize: "sm",
			fontWeight: "normal",
			variant: "outline",
		},
	},
)
const textareaContainerVariants = cva("inline-flex flex-1 rounded-md", {
	variants: {
		variant: {
			outline: "shadow-sm",
			settings: "shadow-sm rounded-[10px]",
			portal: "",
			borderless: "",
		},
	},
	defaultVariants: {
		variant: "outline",
	},
})

export interface TextareaProps
	extends TextareaAutosizeProps, VariantProps<typeof textareaVariants> {
	label?: ReactNode
	name: string
	inputClassName?: string
	suffix?: ReactNode
	hint?: ReactNode
	errorMessage?: ReactNode
	showErrorMessage?: boolean
	isRequired?: boolean
	noResize?: boolean
}

export const Textarea = memo(
	forwardRef<HTMLTextAreaElement, TextareaProps>(
		(
			{
				className,
				label,
				inputClassName,
				name,
				suffix,
				hint,
				errorMessage,
				showErrorMessage = true,
				variant,
				textSize,
				noResize,
				fontWeight,
				...props
			},
			ref,
		) => {
			return (
				<div className={cn("flex flex-col", className)}>
					{label && (
						<Label className="mb-2.5 text-left" htmlFor={name}>
							{label}
						</Label>
					)}
					<div className={textareaContainerVariants({ variant })}>
						<div className="relative flex-1">
							<TextareaAutosize
								className={cn(
									textareaVariants({ variant, textSize, fontWeight }),
									Boolean(suffix ?? errorMessage) && "pr-9",
									noResize && "resize-none",
									Boolean(errorMessage) &&
										"!ring-red-300 focus:!ring-red-500 dark:!ring-red-500/50 dark:focus:!ring-red-500",
									inputClassName,
								)}
								ref={ref}
								name={name}
								id={name}
								aria-invalid={!!errorMessage}
								aria-describedby={`${name}-error`}
								{...props}
							/>
							{(suffix ?? errorMessage) && (
								<div
									className={cn(
										"pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3",
										errorMessage ? "text-red-500" : "text-secondary",
									)}
								>
									{errorMessage && showErrorMessage ? (
										<AlertCircleIcon className="h-4 w-4" />
									) : (
										suffix
									)}
								</div>
							)}
						</div>
					</div>
					{hint && (
						<div className={cn("mt-1.5 text-left text-sm text-secondary")}>
							{hint}
						</div>
					)}
					<AnimatePresence>
						{errorMessage && showErrorMessage && (
							<motion.p
								initial={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								animate={{
									opacity: 1,
									height: "auto",
									marginTop: "0.5rem",
								}}
								exit={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								transition={{ duration: 0.1 }}
								className="mt-1.5 text-sm text-red-600"
								id={`${name}-error`}
							>
								{errorMessage}
							</motion.p>
						)}
					</AnimatePresence>
				</div>
			)
		},
	),
)
